/**
 * Survey Preview Helper
 * Transforms survey custom fields into FormBuilder config for preview
 */

import { useState } from 'react';
import { useField } from 'formik';
import { Box, Flex, Icon, Spinner } from '@chakra-ui/react';
import * as yup from 'yup';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { FileAttachment04 } from '@kula-ai/icons/outline/FileAttachment04';
import { Download01 } from '@kula-ai/icons/outline/Download01';
import { useShowToast } from '@/hooks';
import { KText } from '@/ui-library/components/text';
import { FileUpload } from '@/components/ats/FileUpload/FileUpload';
import { http } from 'assessment/query/http';

// Extend window interface for file uploads
declare global {
  interface Window {
    accountId?: string;
    surveyFileUploads?: Record<string, any>;
  }
}

const containerStyles = {
  margin: '0px',
};

const acceptedFiles = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  'text/plain',
  'image/jpeg',
  'image/png',
];

const labelStyles = {
  fontSize: '14px',
  fontWeight: 'SemiBold',
  minWidth: 'unset',
  width: '100%',
  marginRight: '0px',
  marginBottom: '6px',
  '& span': {
    color: 'red',
    marginLeft: '4px',
  },
};

const subLabelStyles = {
  marginRight: '0px',
  fontSize: '12px',
  color: 'secondary-600',
  marginBottom: '6px',
};

const formControlStyles = {
  borderBottom: '1px solid',
  borderColor: 'border-color',
  pb: '24px',
  pt: '24px',
  mb: '0px',
};

const formErrorMessageStyles = {
  mt: '0.5rem',
  mb: '-1rem',
};

const editorStyles = {
  enableCustomVariables: false,
  containerStyles: { width: '100%', ...containerStyles },
  subLabelStyles,
  customEditorClassName: 'survey-editor-class',
  editorContainerStyles: {
    border: 'none',
    height: '50px',
  },
  editorWraperStyles: {
    padding: '16px',
    border: '1px solid',
    borderColor: 'border-color',
    borderRadius: 'sm',
    overflow: 'hidden',
  },
  editorStyles: {
    padding: '0px',
    minHeight: '0px !important',
    paddingBottom: '0px !important',
  },
  toolbarWrapperStyles: { marginLeft: '-10px' },
  pluginAlignment: 'bottom',
  formControlStyles,
  formErrorMessageStyles,
};

const inputStyles = {
  formControlStyles,
  labelStyles,
  subLabelStyles,
  inputStyles: {
    borderColor: 'border-color',
  },
  errorMessage: 'This is required field',
  formErrorMessageStyles,
  containerStyles,
};

/**
 * Map survey field kinds to FormBuilder field types
 */
const fieldTypeMap: Record<string, any> = {
  string: {
    type: 'kInput',
    ...inputStyles,
    placeholder: 'Enter your answer',
  },
  text: {
    type: 'editor',
    ...editorStyles,
    placeholder: 'Enter your answer',
    hasCodeBlock: false,
    resize: true,
  },
  number: {
    type: 'kInput',
    inputType: 'number',
    ...inputStyles,
  },
  date: {
    type: 'date',
    formControlStyles,
    labelStyles: {
      ...labelStyles,
      width: 'fit-content',
    },
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  url: {
    type: 'kInput',
    inputType: 'url',
    ...inputStyles,
  },
  enum_boolean: {
    type: 'radio2',
    options: [
      { value: 'Y', label: 'Yes' },
      { value: 'N', label: 'No' },
    ],
    formControlStyles,
    radioStyles: { variant: 'ats-primary-sm' },
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
    styles: { direction: 'row', ml: '0px', gap: '16px' },
    placeholder: 'Select your answer',
  },
  rel_tag: {
    // Single select
    type: 'kSelect',
    formControlStyles,
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
    styles: { direction: 'column', ml: '0px', gap: '8px' },
    valueKey: 'value',
    labelKey: 'label',
    isClearable: false,
    placeholder: 'Select your answer',
  },
  rel_tag_multi: {
    // Multi select
    type: 'kSelect',
    isMulti: true,
    formControlStyles,
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
    styles: { direction: 'column', ml: '0px', gap: '8px' },
    valueKey: 'value',
    labelKey: 'label',
    isClearable: false,
    placeholder: 'Select your answer',
  },
  rel_file: {
    type: 'function',
    function: true,
    Field: (props) => {
      const { formik, ...fieldProps } = props;

      // Simple upload function
      const uploadFile = async (file: File, accountId: string) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await http.post(
          `/accounts/direct_upload_file?account_id=${accountId}`,
          formData
        );

        // Cast to any to handle API response structure
        const responseData = response as any;
        if (responseData?.errors?.length) {
          return { error: responseData?.errors };
        }

        // The API response structure might be nested, let's check both response.data and response
        const apiData = response.data || response;
        return apiData;
      };

      const [uploadedFiles, setUploadedFiles] = useState([]);
      const [isUploading, setIsUploading] = useState(false);
      const toast = useShowToast();

      // Get field name from fieldProps or use a default
      const fieldName = fieldProps?.name || 'defaultField';
      //eslint-disable-next-line
      const [fieldValue, , helpers] = useField(fieldName);
      const { setValue, setTouched, setError } = helpers;

      // Use formik's setFieldValue for more reliable updates
      const setFieldValue = formik?.setFieldValue;

      // Get accountId and questionId with proper null checks
      const accountId =
        fieldProps?.accountId || window.accountId || 'default-account';
      const questionId =
        fieldProps?.questionId ||
        (fieldProps?.name ? fieldProps.name.replace('field_', '') : 'unknown');

      const handleFileUpload = async (file) => {
        try {
          setIsUploading(true);
          setError('');

          // Skip actual upload if in preview mode
          if (fieldProps?.isPreview) {
            // Create a mock file object for preview
            const mockFileData = {
              id: `preview-${Date.now()}`,
              filename: file.name,
              content_type: file.type,
              byte_size: file.size,
              url: URL.createObjectURL(file),
            };

            const uploadedFile = {
              id: mockFileData.id,
              filename: mockFileData.filename,
              content_type: mockFileData.content_type,
              byte_size: mockFileData.byte_size,
              url: mockFileData.url,
              questionId,
              originalFile: file,
            };

            // Replace any existing file with the new uploaded file (only one file allowed)
            setUploadedFiles([uploadedFile]);
            // Store only the file ID as string for form validation
            setValue(mockFileData.id);
            // Also use formik's setFieldValue for more reliable updates
            if (setFieldValue) {
              setFieldValue(fieldName, mockFileData.id);
            }

            setIsUploading(false);
            return;
          }

          const response = await uploadFile(file, accountId);
          if (response?.error?.length) {
            const errorMessage = response.error[0] || 'File upload failed';
            setError(errorMessage);
            toast({
              title: 'File upload failed',
              status: 'error',
            });
            setIsUploading(false);
            return;
          }

          // Access the nested data structure
          const fileData = response.data || response;

          const uploadedFile = {
            id: fileData.id,
            filename: fileData.filename,
            content_type: fileData.content_type,
            byte_size: fileData.byte_size,
            url: fileData.url,
            questionId,
            originalFile: file,
          };

          // Replace any existing file with the new uploaded file (only one file allowed)
          setUploadedFiles([uploadedFile]);
          // Store only the file ID as string for form validation
          setValue(fileData.id);
          // Also use formik's setFieldValue for more reliable updates
          if (setFieldValue) {
            setFieldValue(fieldName, fileData.id);
          }

          // Store in global object for later access with question ID as key
          if (!window.surveyFileUploads) {
            window.surveyFileUploads = {};
          }
          // Store in format: {questionId: fileId}
          window.surveyFileUploads[questionId] = fileData.id;
        } catch (error) {
          const errorMessage = error.message || 'Upload failed';
          setError(errorMessage);
          toast({
            title: 'File upload failed',
            description: errorMessage,
            status: 'error',
          });
        } finally {
          setIsUploading(false);
        }
      };

      // Early return if essential props are missing
      if (!fieldProps || !fieldProps.name) {
        console.error(
          'FileUpload: FieldProps or field name is missing',
          fieldProps
        );
        return (
          <div
            style={{ padding: '20px', border: '1px solid red', margin: '10px' }}
          >
            <div>Error: Field configuration is missing</div>
            <div>FieldProps: {JSON.stringify(fieldProps, null, 2)}</div>
          </div>
        );
      }

      const handleUpload = (selectedFiles, unAcceptedFiles) => {
        if (unAcceptedFiles?.length) {
          setError('Invalid file type');
          return;
        }

        // Only allow one file - take the first file if multiple are selected
        if (selectedFiles?.length > 0) {
          const file = selectedFiles[0];
          // Clear any existing files before uploading new one
          if (uploadedFiles.length > 0) {
            removeFile();
          }
          handleFileUpload(file);
        }
      };

      const removeFile = () => {
        setUploadedFiles([]);
        setValue('');
        // Also use formik's setFieldValue for more reliable updates
        if (setFieldValue) {
          setFieldValue(fieldName, '');
        }
        setTouched(true);

        // Remove from global object
        if (window.surveyFileUploads) {
          delete window.surveyFileUploads[questionId];
        }
      };

      const renderFilePreview = () => {
        if (uploadedFiles.length === 0) return null;

        return (
          <Box
            border="1px solid"
            borderColor="border-color"
            borderRadius="sm"
            p="12px"
            bg="base-white"
            mt="12px"
          >
            {uploadedFiles.map((file, index) => (
              <Flex
                key={index}
                alignItems="center"
                justifyContent="space-between"
                gap="12px"
                p="8px"
                border="1px solid"
                borderColor="border-color"
                borderRadius="sm"
                bg="secondary-50"
              >
                <Flex alignItems="center" gap="8px" flex="1">
                  {isUploading ? (
                    <Spinner size="sm" color="primary-600" />
                  ) : (
                    <Icon
                      as={FileAttachment04}
                      width="16px"
                      height="16px"
                      color="secondary-600"
                    />
                  )}
                  <KText
                    size="sm-regular"
                    color="secondary-900"
                    flex="1"
                    noOfLines={1}
                  >
                    {file.originalFile?.name || 'Uploaded file'}
                  </KText>
                </Flex>
                <Flex alignItems="center" gap="8px">
                  {file?.url && (
                    <Icon
                      as={Download01}
                      width="16px"
                      height="16px"
                      cursor="pointer"
                      onClick={() => window.open(file.url, '_blank')}
                    />
                  )}
                  <Icon
                    as={Trash01}
                    width="16px"
                    height="16px"
                    cursor="pointer"
                    onClick={removeFile}
                  />
                </Flex>
              </Flex>
            ))}
          </Box>
        );
      };

      return (
        <Box
          borderBottom={fieldProps.isLastField ? 'none' : '1px solid'}
          borderColor="border-color"
          pb={fieldProps.isLastField ? '0px' : '24px'}
          pt="24px"
          mb="0px"
        >
          {/* @ts-expect-error - Complex union type */}
          <Flex flexDir="column" gap="10px">
            {/* Field Label */}
            {fieldProps.label && (
              <Flex
                alignItems="center"
                gap="4px"
                mb="8px"
                position="relative"
                top="8px"
              >
                <KText size="sm-medium" color="secondary-900">
                  {fieldProps.label}
                  {fieldProps.required && (
                    <Box as="span" color="red" ml="4px">
                      *
                    </Box>
                  )}
                </KText>
              </Flex>
            )}

            {/* Field Description */}
            {fieldProps.subLabel && (
              <KText size="sm-regular" color="secondary-600" mb="12px">
                {fieldProps.subLabel}
              </KText>
            )}

            {isUploading ? (
              <Flex
                alignItems="center"
                justifyContent="center"
                p="40px"
                border="1px dashed"
                borderColor="border-color"
                borderRadius="sm"
                bg="secondary-25"
              >
                {/* @ts-expect-error - Complex union type */}
                <Spinner width="24px" height="24px" color="primary-600" />
              </Flex>
            ) : (
              <>
                {uploadedFiles.length === 0 && (
                  <FileUpload
                    maxFiles={1}
                    acceptedFiles={acceptedFiles.join(', ')}
                    description={
                      'Supported : Documents (PDF, Word, Excel and more), Images (JPG, PNG and more), Text & Archive files'
                    }
                    handleUpload={handleUpload}
                    primaryColor="primary-500"
                  />
                )}
                {renderFilePreview()}
              </>
            )}
          </Flex>
        </Box>
      );
    },
    formControlStyles,
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
    maxFiles: 1,
    acceptedFiles: '',
    description: 'Drag-and-drop or click to upload',
  },
  star_rating: {
    type: 'star',
    min: 1,
    max: 5,
    formControlStyles,
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  nps_rating: {
    type: 'rating',
    min: 0,
    max: 10,
    isNPS: true,
    formControlStyles,
    labelStyles: {
      ...labelStyles,
      display: 'none', // Hide label from FormBuilder for NPS
    },
    subLabelStyles: {
      ...subLabelStyles,
      display: 'none', // Hide subLabel from FormBuilder for NPS - it will be shown in RatingField
    },
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  scale_rating: {
    type: 'rating',
    min: 0,
    max: 10,
    formControlStyles,
    labelStyles,
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
};

/**
 * Transform survey custom fields to FormBuilder config
 */
export const transformSurveyToFormConfig = (
  customFields: any[],
  isPreview = false
) => {
  if (!customFields || customFields.length === 0) {
    return [];
  }

  const sortedFields = customFields.sort((a, b) => a.position - b.position);

  const formConfig = sortedFields.map((field, index) => {
    // Determine field type - check if rel_tag with multi: true
    let fieldKind = field.kind;
    if (field.kind === 'rel_tag' && field.multi === true) {
      fieldKind = 'rel_tag_multi';
    }

    const fieldConfig = fieldTypeMap[fieldKind] || fieldTypeMap.string;
    const isLastField = index === sortedFields.length - 1;

    // Last field should not have bottom border
    const customFormControlStyles = isLastField
      ? {
          ...formControlStyles,
          borderBottom: 'none',
          pb: '0px',
        }
      : formControlStyles;

    const config: any = {
      name: `field_${field.id}`,
      label: field.label,
      isRequired: field.required || false,
      subLabel: field.description || '',
      ...fieldConfig,
      formControlStyles: customFormControlStyles,
      // Dynamic error message based on field label
      errorMessage: `${field.label} is required`,
    };

    // Determine placeholder based on field type if not already set
    if (!config.placeholder && !field.placeholder) {
      // Choice questions: rel_tag, rel_tag_multi, enum_boolean
      if (
        field.kind === 'rel_tag' ||
        field.kind === 'rel_tag_multi' ||
        field.kind === 'enum_boolean'
      ) {
        config.placeholder = 'Select your answer';
      }
      // Text questions: string, text
      else if (field.kind === 'string' || field.kind === 'text') {
        config.placeholder = 'Enter your answer';
      }
    } else if (field.placeholder) {
      // Use field's custom placeholder if provided
      config.placeholder = field.placeholder;
    }

    // In preview mode, remove validation but keep isRequired for the asterisk display
    if (isPreview) {
      delete config.errorMessage;
      delete config.validation;
      delete config.optionalValidation;
      // Set a flag to disable validation errors (for DateField and other components)
      config.disableValidation = true;
      // Keep isPreviewMode for backward compatibility if needed elsewhere
      config.isPreviewMode = true;
    }

    // Add props for survey file upload
    if (field.kind === 'rel_file') {
      config.questionId = field.id.toString();
      config.accountId = window.accountId || 'default-account';
      // Pass the field configuration as props
      config.props = {
        name: config.name,
        label: config.label,
        required: config.isRequired,
        questionId: config.questionId,
        accountId: config.accountId,
        acceptedFiles: config.acceptedFiles,
        description: config.description,
        isLastField: isLastField,
        isPreview: isPreview,
      };

      // Add validation only if not in preview mode
      if (!isPreview && field.required) {
        config.validation = yup.string().required(`${field.label} is required`);
      }
    }

    // Handle select options for rel_tag (both single and multi)
    if (field.kind === 'rel_tag') {
      if (field.values && field.values.length > 0) {
        config.options = field.values.map((val: any) => ({
          label: val.value,
          value: val.id.toString(),
        }));
      } else {
        config.options = [];
      }

      // Handle multi-select based on field.multi property
      if (field.multi === true) {
        config.isMulti = true;
      }
    }

    // Handle rating fields (scale_rating, nps_rating, star_rating) - set min/max values
    if (
      field.kind === 'scale_rating' ||
      field.kind === 'nps_rating' ||
      field.kind === 'star_rating'
    ) {
      if (field.options) {
        // Use options from field if provided
        if (field.kind === 'star_rating') {
          config.min = field.options.min_value || 1;
          config.max = field.options.max_value || 5;
        } else {
          config.min = field.options.min_value || 0;
          config.max = field.options.max_value || 10;
        }
      } else {
        // Default values based on field type
        if (field.kind === 'star_rating') {
          config.min = 1;
          config.max = 5;
        } else {
          config.min = 0;
          config.max = 10;
        }
      }
    }

    // Handle URL validation (skip in preview mode)
    if (field.kind === 'url' && !isPreview) {
      if (field.required) {
        config.validation = yup
          .string()
          .url('Please enter a valid URL')
          .required(`${field.label} is required`);
      } else {
        // For non-required URL fields, allow empty strings or valid URLs
        config.validation = yup
          .string()
          .test('is-url-or-empty', 'Please enter a valid URL', (value) => {
            if (!value || value === '') return true; // Allow empty
            return yup.string().url().isValidSync(value); // Validate if not empty
          })
          .nullable();
      }
    }

    // Handle Number validation (skip in preview mode)
    if (field.kind === 'number' && !isPreview) {
      if (field.required) {
        config.validation = yup
          .number()
          .typeError('Please enter a valid number')
          .required(`${field.label} is required`);
      } else {
        // For non-required number fields, allow empty or valid numbers
        config.validation = yup
          .number()
          .typeError('Please enter a valid number')
          .nullable()
          .transform((value, originalValue) => {
            // If empty string, return null instead of NaN
            return originalValue === '' ? null : value;
          });
      }
    }

    return config;
  });

  return formConfig;
};

/**
 * Get initial values for the form
 */
export const getInitialValuesForSurvey = (customFields: any[]) => {
  if (!customFields || customFields.length === 0) {
    return {};
  }

  const initialValues: Record<string, any> = {};

  customFields.forEach((field) => {
    const fieldName = `${field.id}`;

    // Set default values based on field type
    if (field.kind === 'rel_tag' && field.multi === true) {
      // Multi-select should have empty array
      initialValues[fieldName] = [];
    } else if (field.kind === 'enum_boolean') {
      initialValues[fieldName] = '';
    } else if (field.kind === 'number') {
      initialValues[fieldName] = '';
    } else if (
      field.kind === 'scale_rating' ||
      field.kind === 'nps_rating' ||
      field.kind === 'star_rating'
    ) {
      // Rating fields start with null/undefined
      initialValues[fieldName] = null;
    } else {
      initialValues[fieldName] = '';
    }
  });

  return initialValues;
};
